import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import VideoCard from "@/components/feature/VideoCard";
import SeoMeta from "@/components/feature/SeoMeta";
import { allVideos, categories } from "@/mocks/videos";

const catName = (id: string) => categories.find((c) => c.id === id)?.name || "吃瓜视频";

export default function VideoDetailPage() {
  const { id } = useParams();
  const video = allVideos.find((v) => v.id === id);

  if (!video) {
    return (
      <div className="bg-background-50 min-h-screen">
        <SiteHeader />
        <div className="mx-auto max-w-7xl px-4 md:px-6 pt-32 pb-24 text-center">
          <h1 className="text-2xl font-black text-foreground-950">视频不存在</h1>
          <Link to="/" className="mt-4 inline-block text-primary-600 font-semibold">
            返回首页
          </Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const related = allVideos.filter((v) => v.category === video.category && v.id !== video.id).slice(0, 4);
  const others = related.length < 4 ? allVideos.filter((v) => v.id !== video.id && !related.includes(v)).slice(0, 4 - related.length) : [];

  return (
    <div className="bg-background-50 min-h-screen">
      <SeoMeta
        title={`${video.title} - 国产吃瓜视频`}
        description={`${video.desc} 该${catName(video.category)}吃瓜视频目前已有 ${video.views} 次观看，聚合国产吃瓜视频合集、精选与推荐内容。`}
        type="video.other"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "VideoObject",
            name: video.title,
            description: video.desc,
            thumbnailUrl: video.cover,
            uploadDate: video.date,
            duration: `PT${video.duration.replace(":", "M")}S`,
            interactionStatistic: {
              "@type": "InteractionCounter",
              interactionType: { "@type": "WatchAction" },
              userInteractionCount: video.views,
            },
            author: { "@type": "Organization", name: "国产吃瓜视频" },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://www.readdy.ai/" },
              { "@type": "ListItem", position: 2, name: catName(video.category), item: `https://www.readdy.ai/category/${video.category}` },
              { "@type": "ListItem", position: 3, name: video.title },
            ],
          },
        ]}
      />
      <SiteHeader />

      <div className="mx-auto max-w-7xl px-4 md:px-6 pt-24 md:pt-32 pb-14 md:pb-20">
        <nav className="flex items-center gap-1 text-sm text-foreground-500 mb-5 flex-wrap">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to={`/category/${video.category}`} className="hover:text-primary-600">
            {catName(video.category)}
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-700 line-clamp-1">{video.title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            {/* 播放器 */}
            <div className="relative rounded-2xl overflow-hidden bg-black aspect-video">
              <img
                src={video.cover}
                alt={video.title}
                className="absolute inset-0 w-full h-full object-cover opacity-90"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              <button
                type="button"
                aria-label="播放视频"
                className="absolute inset-0 m-auto inline-flex w-16 h-16 md:w-20 md:h-20 items-center justify-center rounded-full bg-primary-500/90 text-background-50 hover:bg-primary-500 hover:scale-110 transition-all"
              >
                <i className="ri-play-fill text-3xl"></i>
              </button>
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-background-50 text-sm">
                <span className="inline-flex items-center gap-1">
                  <i className="ri-time-line"></i> {video.duration}
                </span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-eye-line"></i> {video.views}
                </span>
              </div>
            </div>

            {/* 标题信息 */}
            <div className="mt-6">
              <h1 className="text-xl md:text-2xl font-black text-foreground-950 leading-snug">{video.title}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Link
                  to={`/category/${video.category}`}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-sm font-semibold hover:bg-secondary-200 transition-colors"
                >
                  {catName(video.category)}
                </Link>
                {video.tags.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-sm"
                  >
                    <i className="ri-price-tag-3-line"></i> {t}
                  </span>
                ))}
                <span className="ml-auto inline-flex items-center gap-1 text-sm text-foreground-500">
                  <i className="ri-calendar-line"></i> {video.date}
                </span>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-bold whitespace-nowrap hover:bg-primary-600 transition-colors"
                >
                  <i className="ri-thumb-up-line"></i> 点赞
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-bold whitespace-nowrap hover:bg-background-200 transition-colors"
                >
                  <i className="ri-share-forward-line"></i> 分享
                </button>
              </div>
            </div>

            {/* 简介 */}
            <div className="mt-6 bg-background-100 rounded-xl p-5">
              <h2 className="font-bold text-foreground-900 mb-2 flex items-center gap-2">
                <i className="ri-file-text-line text-primary-500"></i> 视频简介
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed">{video.desc}</p>
            </div>
          </div>

          {/* 相关推荐 */}
          <aside>
            <h2 className="font-heading text-lg md:text-xl font-black text-foreground-950 mb-4">相关推荐</h2>
            <div className="space-y-4">
              {[...related, ...others].map((v) => (
                <VideoCard key={v.id} video={v} />
              ))}
            </div>
          </aside>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
}